import type { ApiResult } from "./contracts";

/**
 * Клиент журнала входов (D-2, экран «Журнал входов»). Доступен только юристу и админу — сервер
 * проверяет право `login_audit:view` на маршруте; скрытие раздела в навигации это лишь дублирует.
 * Только чтение. Пути относительные (`/api/...`), сессия — cookie HttpOnly (Р-5).
 */

export type LoginOutcome = "success" | "failure";
export type LoginFailureReason =
  "no_login" | "bad_password" | "account_disabled" | "no_password" | "throttled";

/** Строка журнала — ответ GET /api/login-attempts. */
export interface LoginAttempt {
  readonly id: string;
  /** Момент попытки — ISO-строка с таймзоной; в пояс Омска переводит UI (formatDateTime). */
  readonly at: string;
  readonly login: string;
  readonly outcome: LoginOutcome;
  readonly reason: LoginFailureReason | null;
  readonly ip: string | null;
}

export interface LoginAttemptPage {
  readonly items: LoginAttempt[];
  readonly total: number;
}

/** Параметры фильтра. Пустые поля не сужают выборку. */
export interface LoginAttemptFilter {
  readonly login?: string;
  readonly outcome?: LoginOutcome;
  readonly reason?: LoginFailureReason;
  readonly dateFrom?: string;
  readonly dateTo?: string;
  readonly page: number;
  readonly limit: number;
}

/** Русские наименования итогов и цветовые токены (docs/07; правило 9). */
export const OUTCOME_META: Record<LoginOutcome, { readonly label: string; readonly cls: string }> =
  {
    success: { label: "Успех", cls: "text-done-fg bg-done-bg" },
    failure: { label: "Отказ", cls: "text-overdue-fg bg-overdue-bg" },
  };

/** Русские наименования причин отказа. */
export const REASON_LABELS: Record<LoginFailureReason, string> = {
  no_login: "Нет логина",
  bad_password: "Неверный пароль",
  account_disabled: "Учётка отключена",
  no_password: "Пароль не задан",
  throttled: "Блокировка (серия неудач)",
};

/** Наименование причины (или «—» для успеха/пустой причины). */
export function reasonLabel(reason: LoginFailureReason | null): string {
  return reason ? REASON_LABELS[reason] : "—";
}

async function toFailure(res: Response): Promise<Extract<ApiResult<never>, { ok: false }>> {
  if (res.status === 401) {
    return { ok: false, reason: "unauthorized", message: "Сессия истекла. Войдите заново." };
  }
  const body = (await res.json().catch(() => null)) as { message?: string } | null;
  const message = body?.message;
  if (res.status === 403)
    return { ok: false, reason: "forbidden", message: message ?? "Недостаточно прав." };
  if (res.status === 400) {
    return { ok: false, reason: "validation", message: message ?? "Проверьте параметры фильтра." };
  }
  return {
    ok: false,
    reason: "network",
    message: message ?? "Не удалось загрузить журнал входов.",
  };
}

function toQuery(filter: LoginAttemptFilter): string {
  const params = new URLSearchParams();
  if (filter.login) params.set("login", filter.login);
  if (filter.outcome) params.set("outcome", filter.outcome);
  if (filter.reason) params.set("reason", filter.reason);
  if (filter.dateFrom) params.set("dateFrom", filter.dateFrom);
  if (filter.dateTo) params.set("dateTo", filter.dateTo);
  params.set("page", String(filter.page));
  params.set("limit", String(filter.limit));
  return params.toString();
}

/** Страница журнала входов по фильтру (GET /api/login-attempts). */
export async function fetchLoginAttempts(
  filter: LoginAttemptFilter,
): Promise<ApiResult<LoginAttemptPage>> {
  let res: Response;
  try {
    res = await fetch(`/api/login-attempts?${toQuery(filter)}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as LoginAttemptPage | null;
  return { ok: true, value: { items: body?.items ?? [], total: body?.total ?? 0 } };
}
